import {
  AnomalyApiError,
  apiMessage,
  audience,
  rolesSummary,
} from '@app/features/anomalies/anomaly-presentation';
import {
  addDays,
  formatBound,
  formatDate,
  formatEndDate,
} from '@app/features/anomalies/paris-time';
import { UserRole } from '@app/features/users/user.model';
import { Announcement, ANNOUNCEMENT_DEFAULT_DAYS, VisibleAnnouncement } from './announcement.model';

export interface AnnouncementSections {
  displayed: Announcement[];
  scheduled: Announcement[];
  ended: Announcement[];
  archived: Announcement[];
}

/** Le menu de la direction : ses annonces, rangées selon leur phase à l'heure du serveur. */
export function announcementDirectionSections(announcements: Announcement[]): AnnouncementSections {
  const current = announcements.filter((a) => !a.archived);
  return {
    displayed: current.filter((a) => a.phase === 'DISPLAYED'),
    scheduled: current.filter((a) => a.phase === 'SCHEDULED'),
    ended: current.filter((a) => a.phase === 'ENDED'),
    archived: announcements.filter((a) => a.archived),
  };
}

/** `Jusqu’au 02/12 · 3 rôles`, `Du 05/01 au 04/02 · tous les rôles`. */
export function announcementSubtitle(announcement: Announcement): string {
  const roles = rolesSummary(announcement.targetRoles);
  const until = formatEndDate(announcement.displayedUntil);
  switch (announcement.phase) {
    case 'SCHEDULED':
      return `Du ${formatDate(announcement.displayedFrom)} au ${until} · ${roles}`;
    case 'ENDED':
      return `Plus affichée depuis le ${until} · ${roles}`;
    default:
      return `Jusqu’au ${until} · ${roles}`;
  }
}

/** `depuis le 02/11`, à côté du titre du bandeau. */
export function announcementMeta(announcement: Pick<VisibleAnnouncement, 'displayedFrom'>): string {
  return `depuis le ${formatDate(announcement.displayedFrom)}`;
}

export interface AnnouncementAudienceInput {
  targetRoles: UserRole[];
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
}

/**
 * La phrase du dialogue de saisie qui dit à qui et jusqu'à quand l'annonce sera affichée. Sans date
 * de fin, trente jours à partir de son début ; `now` est l'heure de Paris.
 */
export function announcementAudience(input: AnnouncementAudienceInput, now: string): string {
  if (!input.targetRoles.length) return 'Choisissez au moins un rôle cible.';
  const start = input.startDate ? `${input.startDate}T${input.startTime ?? '00:00'}:00` : now;
  const from = input.startDate
    ? `à partir du ${formatBound(input.startDate, input.startTime)}`
    : 'dès l’enregistrement';
  const until = input.endDate
    ? formatBound(input.endDate, input.endTime)
    : formatDate(addDays(start, ANNOUNCEMENT_DEFAULT_DAYS));
  return `${audience(input.targetRoles)} verront ce bandeau ${from}, jusqu’au ${until}, et pourront le fermer.`;
}

/** Ce qu'on tentait quand l'API a refusé. */
export type AnnouncementAction = 'save' | 'archive' | 'outcome';

/**
 * Le refus de l'API, dit en mots, comme pour une anomalie. `null` pour les autres erreurs, que la
 * fenêtre d'erreur globale prend en charge.
 */
export function announcementErrorMessage(
  error: AnomalyApiError,
  action: AnnouncementAction,
): string | null {
  switch (error.status) {
    case 400: {
      const message = apiMessage(error);
      return message
        ? `L’enregistrement est refusé : ${message}`
        : 'L’enregistrement est refusé. Vérifiez la saisie, puis réessayez.';
    }
    case 404:
      return action === 'outcome'
        ? 'Cette annonce ne vous est plus affichée : elle a été archivée, ou ne vise plus votre rôle.'
        : 'Cette annonce est introuvable.';
    case 409:
      return 'Cette annonce a été archivée entre-temps : elle ne se modifie plus.';
    default:
      return null;
  }
}
